import { cloneDeep } from 'lodash';
import {
  AccountClient,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA
} from '@nile/graphql';

const getAccessor = (col) => {
  switch (col.name) {
    case 'prioritizedId':
      return 'displayId';
    case 'displayId':
      return 'displayId';
    case 'followers':
      return undefined;
    case 'resolutionTimer':
      return 'resolutionDueTimeLeft';
    case 'taskSummary':
      return 'totalTasks';
    default:
      return col.name;
  }
};

const getMethodName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'getAllCategory';
    case 'subcause':
      return 'getAllCause';
    default:
      return 'name';
  }
};

const getKeyName = (field) => {
  switch (field) {
    case 'subcategory':
      return 'subCategories';
    case 'subcause':
      return 'subCauses';
    default:
      return 'name';
  }
};

const generateOptions = (f, data) => {
  const options = [];
  let allValues = [];
  if (f.isCustom) {
    allValues = data.getCustomFieldByName.options.map((el) => {
      return {
        name: el,
        value: el,
        label: el
      };
    });
    return allValues;
  }
  const methodName = getMethodName(f.name);
  const key = getKeyName(f.name);
  switch (f.name) {
    case 'client.name':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'site.name':
      allValues = data.getClientSiteList.sites.map((el) => {
        return {
          ...el,
          name: el.siteId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'subcategory':
    case 'subcause':
      data[methodName].forEach((el) => {
        const subValues = el[key].map((c) => {
          return {
            name: c.name,
            value: c.name,
            label: c.name
          };
        });
        allValues = allValues.concat(subValues);
      });
      break;
    default:
      allValues = [];
  }
  return allValues;
};

const getType = (col) => {
  switch (col.name) {
    case 'name':
      return 'Link';
    case 'createdBy.name':
      return 'CreatedBy';
    case 'user':
      return 'Name';
    case 'triggeredTime':
      return 'DateTime';
    case 'status':
      return 'Status';
    case 'site':
      return 'Site';
    case 'client.name':
      return 'Client';
    case 'deleteAction':
      return 'DeleteAction';
    default:
      return 'Text';
  }
};

const getRuleAttribute = (f) => {
  switch (f.name) {
    case 'prioritizedId':
      return 'displayId';
    default:
      return f.name;
  }
};

const isEditable = (col) => {
  switch (col.name) {
    default:
      return false;
  }
};

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === getRuleAttribute(f);
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

const isSortable = (col) => {
  switch (col.name) {
    default:
      return true;
  }
};

const isFilterable = (col) => {
  switch (col?.name) {
    case 'name':
    case 'createdBy.name':
    case 'createdBy':
      return true;
    default:
      return false;
  }
};

const checkDynamicQuery = (f) => {
  if (f.isCustom) return true;
  switch (f.name) {
    case 'client.name':
    case 'site.name':
    case 'subcategory':
    case 'subcause':
      return true;
    default:
      return false;
  }
};

const updateConditions = (params) => {
  const { condition, selectedField, viewConditions, setConditions } = params;
  if (condition?.operands?.length) {
    const currentConditions = cloneDeep(viewConditions) || {
      joinOperator: 'AND',
      operands: []
    };
    let updatedCondition = {};
    const ruleIdx = currentConditions.operands.findIndex((el) =>
      selectedField.dataType === 'entity'
        ? el.attribute === `${selectedField.name}.name`
        : el.attribute === getRuleAttribute(selectedField)
    );

    if (ruleIdx > -1) {
      currentConditions.operands[ruleIdx] = condition.operands[0];
      updatedCondition = currentConditions;
    } else {
      updatedCondition = {
        ...currentConditions,
        operands: currentConditions.operands.concat(condition.operands)
      };
    }
    setConditions(updatedCondition);
  } else {
    const currentConditions = cloneDeep(viewConditions);
    if (currentConditions) {
      const ruleIdx = currentConditions.operands.findIndex((el) =>
        selectedField.dataType === 'entity'
          ? el.attribute === `${selectedField.name}.name`
          : el.attribute === getRuleAttribute(selectedField)
      );
      if (ruleIdx > -1) {
        currentConditions.operands.splice(ruleIdx, 1);
        setConditions(
          currentConditions?.operands?.length
            ? {
                ...currentConditions,
                operands: currentConditions.operands
              }
            : null
        );
      }
    }
  }
};

function getQuery(property) {
  switch (property.name) {
    case 'technician':
    case 'createdBy.name':
    case 'createdBy':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

function getNameKey(property) {
  switch (property.name) {
    case 'technician':
    case 'createdBy.name':
    case 'createdBy':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

const fetchValues = async (client, query, params) => {
  const attribute = 'name';
  const nameKey = getNameKey(params.property);
  const res = await client.query({
    query: query,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: attribute || 'name',
              operator: 'startsWith',
              value: params.inputValue
            }
          ]
        }
      }
    }
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];

  return allOptions;
};

const getAsyncOptions = (property) => {
  return { client: AccountClient, query: getQuery(property) };
};

export {
  getDefaultValue,
  checkDynamicQuery,
  isFilterable,
  isSortable,
  getAccessor,
  getType,
  isEditable,
  getRuleAttribute,
  updateConditions,
  getAsyncOptions,
  fetchValues,
  generateOptions
};
